import { ButtonLink } from '../../design/index.js';
import { entryPrice } from '../pricing.js';
import { SalonDiary } from './SalonDiary.js';

/**
 * Act one: the shop sign beside the diary.
 *
 * The promise is lettered narrow and heavy, the way a salon's name is painted
 * across its window, and nothing in it is coloured for emphasis: the size is
 * the emphasis. Beside it stands the salon's own diary for a Saturday, where
 * the product shows what it does before any argument is made (`SalonDiary`).
 *
 * ## Side by side, so the story fits one screen
 *
 * The sign used to run the full width with the diary underneath, and on a
 * laptop the diary started halfway down: the call at 11:02 was on screen, the
 * 16:00 appointment it made was not, and the page's one moment played to
 * nobody. On a wide screen the sign now takes the left half and the diary the
 * right, from the top, so both the call and what it made are in the first
 * screen of a 1366 × 768 laptop (e2e, `layout.spec.ts`). On a phone nothing
 * changes: the sign, the words, the buttons, then the diary.
 *
 * Who it is for is said in the first words of the paragraph rather than in a
 * label above the heading: a label is one more thing to read before the point.
 * What it costs is said under the buttons, from the catalogue, because « how
 * much » is the question a salon owner asks before scrolling ten screens to the
 * price board.
 */
export function Hero({
  startUrl,
  signedIn,
}: {
  readonly startUrl: string;
  readonly signedIn: boolean;
}): React.JSX.Element {
  const entry = entryPrice();
  return (
    <section className="pb-section pt-10 sm:pt-14 lg:pt-12">
      <div className="shell grid gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-6">
          <h1 className="sign text-poster text-ink lg:text-poster-split">
            <span className="block">Vous travaillez.</span>{' '}
            <span className="block">GSI Voice décroche.</span>
          </h1>

          <p className="mt-8 max-w-[34rem] text-lead text-ink-muted">
            Pour les coiffeurs, barbiers et instituts. Quand vous avez les mains prises, GSI Voice
            répond, consulte votre agenda en temps réel et réserve le bon créneau avec le bon
            collaborateur. Quand il faut une personne, l’appel vous est transféré.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <ButtonLink href={startUrl} variant="primary" size="lg">
              {signedIn ? 'Ouvrir le tableau de bord' : 'Créer mon compte'}
            </ButtonLink>
            <ButtonLink href="#demo" variant="secondary" size="lg">
              Demander une démonstration
            </ButtonLink>
          </div>

          <div className="mt-5 space-y-1 text-ui-sm text-ink-muted">
            {entry !== null && (
              <p>
                À partir de <span className="font-medium text-ink">{entry.monthly} HT</span> par
                mois{entry.noSetupFee ? ', sans frais d’installation' : ''}.{' '}
                <a
                  href="#tarifs"
                  className="rounded-control font-medium text-ink underline underline-offset-2"
                >
                  Voir les tarifs
                </a>
              </p>
            )}
            <p>Vous gardez votre numéro. Rien à installer sur votre ordinateur.</p>
          </div>
        </div>

        <div className="lg:col-span-6">
          <SalonDiary />
        </div>
      </div>
    </section>
  );
}
